// frontend/src/lib/conversation.ts
import { ScriptElementType } from '../types/script'
import type {
  PromptResponseSubmission, ScriptExecutionElementDto, SurveyResponseSubmission, TagAssignmentSubmission,
} from '../types/canvasser'

/**
 * [Spec §4.6, Phase 5] The running conversation as a PATH, not an accumulator. The only state is the
 * ordered list of choices the canvasser made. Everything else (the thread, the current step, the
 * answers, the auto-applied tags, the Decision Point responses) is re-derived by walking the script
 * along that path, so truncating the path (Change) prunes every later answer and tag by construction.
 */

export type StepKind = 'say' | 'ask' | 'decide'
export interface ConvStep {
  /** The step's first element id. Stable across walks. */
  id: string
  kind: StepKind
  /** say: the consecutive Text elements; ask/decide: the one element. */
  elements: ScriptExecutionElementDto[]
}

export type Choice =
  | { stepId: string; kind: 'read' }
  | { stepId: string; kind: 'answer'; answerId: string }
  | { stepId: string; kind: 'response'; index: number }
  | { stepId: string; kind: 'skip' }

export type ThreadItem =
  | { type: 'step'; step: ConvStep; choice: Choice; pathIndex: number }
  | { type: 'tag'; tagId: string; label: string }

export interface ScriptResult {
  surveyResponses: SurveyResponseSubmission[]
  tagAssignments: TagAssignmentSubmission[]
  promptResponses: PromptResponseSubmission[]
}

export interface ConversationWalk {
  thread: ThreadItem[]
  /** The step waiting for a choice; null once the script has run out. */
  current: ConvStep | null
  /** How many path entries the walk used. Anything after them no longer fits the script and is dropped. */
  consumed: number
  results: ScriptResult
}

const isPrompt = (el: ScriptExecutionElementDto) =>
  el.elementType === ScriptElementType.ConditionalBranch && !!el.promptText && (el.promptResponses?.length ?? 0) > 0

function firstElementId(elements: ScriptExecutionElementDto[]): string | undefined {
  let best: ScriptExecutionElementDto | undefined
  for (const el of elements) if (!best || el.displayOrder < best.displayOrder) best = el
  return best?.id
}

/** Auto conditions: Answer ("questionId:answerId"), Tag (applied on this path), Attribute (server-resolved). */
export function evaluateCondition(
  el: ScriptExecutionElementDto, answers: Map<string, string>, tags: Map<string, string>,
): boolean {
  const field = el.conditionalField ?? ''
  const value = el.conditionalValue ?? ''
  if (field === 'Attribute') return el.conditionalResolvedOutcome === true
  if (field === 'Tag') return tags.has(value)
  if (field === 'Answer') {
    const [q, a] = value.split(':')
    return !!q && !!a && answers.get(q) === a
  }
  return false
}

function stepFrom(el: ScriptExecutionElementDto, byId: Map<string, ScriptExecutionElementDto>, seen: Set<string>): ConvStep {
  if (el.elementType === ScriptElementType.Text) {
    const group = [el]
    let next = el.nextElementId ?? undefined
    while (next && !seen.has(next)) {
      const n = byId.get(next)
      if (!n || n.elementType !== ScriptElementType.Text) break
      seen.add(next)
      group.push(n)
      next = n.nextElementId ?? undefined
    }
    return { id: el.id, kind: 'say', elements: group }
  }
  return { id: el.id, kind: el.elementType === ScriptElementType.SurveyQuestion ? 'ask' : 'decide', elements: [el] }
}

function fits(step: ConvStep, c: Choice): boolean {
  if (c.stepId !== step.id) return false
  const el = step.elements[0]
  if (step.kind === 'say') return c.kind === 'read'
  if (c.kind === 'skip') return true
  if (step.kind === 'ask') return c.kind === 'answer' && (el.answers ?? []).some(a => a.id === c.answerId)
  return c.kind === 'response' && Number.isInteger(c.index) && c.index >= 0 && c.index < (el.promptResponses?.length ?? 0)
}

function nextAfter(step: ConvStep, c: Choice): string | undefined {
  const el = step.elements[step.elements.length - 1]
  if (c.kind === 'answer') return el.answerBranches?.[c.answerId] ?? el.nextElementId ?? undefined
  if (c.kind === 'response') return el.promptResponses?.[c.index]?.targetElementId ?? el.nextElementId ?? undefined
  return el.nextElementId ?? undefined
}

export function walkConversation(elements: ScriptExecutionElementDto[], path: Choice[]): ConversationWalk {
  const byId = new Map(elements.map(e => [e.id, e] as const))
  const seen = new Set<string>()
  const thread: ThreadItem[] = []
  const answers = new Map<string, string>()
  const tags = new Map<string, string>()
  const prompts: PromptResponseSubmission[] = []
  let used = 0
  let current: ConvStep | null = null
  let id = firstElementId(elements)

  while (id && !seen.has(id)) {
    const el = byId.get(id)
    if (!el) break
    seen.add(id)
    if (el.elementType === ScriptElementType.TagAssignment) {
      if (el.tagId && !tags.has(el.tagId)) {
        tags.set(el.tagId, el.tagLabel ?? '')
        thread.push({ type: 'tag', tagId: el.tagId, label: el.tagLabel ?? '' })
      }
      id = el.nextElementId ?? undefined
      continue
    }
    if (el.elementType === ScriptElementType.ConditionalBranch && !isPrompt(el)) {
      id = (evaluateCondition(el, answers, tags) ? el.nextElementOnTrue : el.nextElementOnFalse) ?? undefined
      continue
    }
    if (el.elementType !== ScriptElementType.Text && el.elementType !== ScriptElementType.SurveyQuestion && !isPrompt(el)) {
      id = el.nextElementId ?? undefined
      continue
    }
    const step = stepFrom(el, byId, seen)
    const choice = path[used]
    if (!choice || !fits(step, choice)) {
      current = step
      break
    }
    thread.push({ type: 'step', step, choice, pathIndex: used })
    used++
    if (choice.kind === 'answer' && el.surveyQuestionId) answers.set(el.surveyQuestionId, choice.answerId)
    if (choice.kind === 'response') {
      prompts.push({ scriptElementId: el.id, promptText: el.promptText ?? null, responseLabel: el.promptResponses![choice.index].label })
    }
    id = nextAfter(step, choice)
  }

  return {
    thread,
    current,
    consumed: used,
    results: {
      surveyResponses: [...answers].map(([surveyQuestionId, answerId]) => ({ surveyQuestionId, answerId })),
      tagAssignments: [...tags.keys()].map(tagId => ({ tagId, assign: true })),
      promptResponses: prompts,
    },
  }
}

/**
 * [P5-5] Steps on the LONGEST remaining path, counting the current one. A condition that can't be
 * decided yet counts its longer branch, so the count only falls as the canvasser answers forward.
 */
export function stepsLeft(elements: ScriptExecutionElementDto[], walk: ConversationWalk): number {
  if (!walk.current) return 0
  const byId = new Map(elements.map(e => [e.id, e] as const))
  const memo = new Map<string, number>()
  const onStack = new Set<string>()
  const longest = (id: string | null | undefined, afterText: boolean): number => {
    if (!id) return 0
    const el = byId.get(id)
    if (!el || onStack.has(id)) return 0
    const key = `${id}|${afterText}`
    const hit = memo.get(key)
    if (hit !== undefined) return hit
    onStack.add(id)
    const best = (ids: (string | null | undefined)[]) => Math.max(0, ...ids.map(n => longest(n, false)))
    let r: number
    if (el.elementType === ScriptElementType.Text) r = (afterText ? 0 : 1) + longest(el.nextElementId, true)
    else if (el.elementType === ScriptElementType.SurveyQuestion) r = 1 + best([el.nextElementId, ...Object.values(el.answerBranches ?? {})])
    else if (isPrompt(el)) r = 1 + best([el.nextElementId, ...(el.promptResponses ?? []).map(p => p.targetElementId)])
    else if (el.elementType === ScriptElementType.ConditionalBranch) {
      r = el.conditionalField === 'Attribute'
        ? longest(el.conditionalResolvedOutcome === true ? el.nextElementOnTrue : el.nextElementOnFalse, false)
        : best([el.nextElementOnTrue, el.nextElementOnFalse])
    } else r = longest(el.nextElementId, false)
    onStack.delete(id)
    memo.set(key, r)
    return r
  }
  return longest(walk.current.id, false)
}

export function sameChoice(a: Choice | undefined, b: Choice | undefined): boolean {
  if (!a || !b || a.stepId !== b.stepId || a.kind !== b.kind) return false
  if (a.kind === 'answer') return a.answerId === (b as typeof a).answerId
  if (a.kind === 'response') return a.index === (b as typeof a).index
  return true
}

/** [P5-1] Re-answer the finished step at path index k. The same choice keeps everything; a new one drops every later choice. */
export function reviseAt(path: Choice[], k: number, c: Choice): Choice[] {
  return sameChoice(path[k], c) ? path : [...path.slice(0, k), c]
}

export function stepText(step: ConvStep, lang: 'en' | 'es'): string {
  const el = step.elements[0]
  if (step.kind === 'say') return step.elements.map(t => (lang === 'es' && t.textContentSpanish) || t.textContent || '').join('\n')
  if (step.kind === 'ask') return (lang === 'es' && el.questionTextSpanish) || el.questionText || ''
  return el.promptText ?? ''
}

export function choiceLabel(step: ConvStep, c: Choice, lang: 'en' | 'es'): string {
  const el = step.elements[0]
  if (c.kind === 'skip') return 'Skipped'
  if (c.kind === 'answer') {
    const a = el.answers?.find(x => x.id === c.answerId)
    return (lang === 'es' && a?.textSpanish) || a?.text || ''
  }
  if (c.kind === 'response') return el.promptResponses?.[c.index]?.label ?? ''
  return ''
}


// ─── Persistence (P5-8): one saved path per conversation, in sessionStorage ───

const PATH_VERSION = 5
const KINDS = new Set(['read', 'answer', 'response', 'skip'])
function isChoice(x: unknown): x is Choice {
  const c = x as Record<string, unknown> | null
  return !!c && typeof c.stepId === 'string' && KINDS.has(c.kind as string)
    && (c.kind !== 'answer' || typeof c.answerId === 'string')
    && (c.kind !== 'response' || Number.isInteger(c.index))
}

/** The saved path, or [] for nothing / junk / an older shape (e.g. the Phase 4 engine's blob). */
export function loadPath(key: string): Choice[] {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return []
    const p = JSON.parse(raw) as { v?: unknown; path?: unknown }
    if (p?.v !== PATH_VERSION || !Array.isArray(p.path)) return []
    const out: Choice[] = []
    for (const x of p.path) {
      if (!isChoice(x)) break
      out.push(x)
    }
    return out
  } catch {
    return []
  }
}

export function savePath(key: string, path: Choice[]): void {
  try { sessionStorage.setItem(key, JSON.stringify({ v: PATH_VERSION, path })) } catch { /* private mode */ }
}

export function clearPath(key: string): void {
  try { sessionStorage.removeItem(key) } catch { /* ignore */ }
}
